import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import TvecLayout from '../../components/TvecLayout';
import { Field, Modal, inputClass } from '../../components/TvecForm';
import axiosInstance from '../../utils/axiosInstance';
import { ALL_COURSES_ID, COURSE_PROGRAMMES, useTvecCourse } from '../../utils/tvecCourse';

const API = '/admin/tvec-batches';
const SELECTED_KEY = 'tvecBatchCategory';
const SUGGESTIONS = ['HND Software', 'HND Networking', 'HND Multimedia', 'HND Cyber Security'];
const STATUSES = ['not started', 'ongoing', 'delivery completed', 'batch completed'];
const STATUS_LABEL = {
  'not started': 'Not started',
  ongoing: 'Ongoing',
  'delivery completed': 'Delivery completed',
  'batch completed': 'Batch completed',
};
const STATUS_STYLE = {
  'not started': 'bg-amber-50 text-amber-700 ring-amber-200',
  ongoing: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  'delivery completed': 'bg-sky-50 text-sky-700 ring-sky-200',
  'batch completed': 'bg-slate-100 text-slate-600 ring-slate-200',
};

const courseShort = (key) => COURSE_PROGRAMMES.find((c) => c.id === key)?.short || key || '';

function CourseChip({ courseKey }) {
  return courseKey ? (
    <span className="rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-sky-700">{courseShort(courseKey)}</span>
  ) : null;
}

const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const readSaved = () => {
  try { return Number(window.localStorage.getItem(SELECTED_KEY)) || null; } catch { return null; }
};
const writeSaved = (id) => {
  try { window.localStorage.setItem(SELECTED_KEY, String(id)); } catch { /* storage can be blocked */ }
};

function CategoryModal({ category, defaultCourse, onClose, onSaved }) {
  // With one course chosen at the top the category goes under it; with "All courses" the user picks.
  const locked = !category && defaultCourse !== ALL_COURSES_ID;
  const [form, setForm] = useState({
    name: category?.name || '',
    courseKey: category?.courseKey || (defaultCourse !== ALL_COURSES_ID ? defaultCourse : ''),
    description: category?.description || '',
    isActive: category ? category.isActive : true,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    const next = {};
    if (!form.courseKey) next.courseKey = 'Select the course for this category.';
    if (!form.name.trim()) next.name = 'Category name is required.';
    if (Object.keys(next).length) { setErrors(next); return; }
    setSaving(true);
    try {
      const res = category
        ? await axiosInstance.put(`${API}/categories/${category.id}`, form)
        : await axiosInstance.post(`${API}/categories`, form);
      onSaved(res.data.data);
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      else Swal.fire({ icon: 'error', title: 'Could not save', text: data?.message || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={category ? 'Edit category' : 'Add category'}
      subtitle="A category groups the batches of one programme, e.g. HND Software."
      onClose={onClose}
      onSubmit={submit}
      saving={saving}
      submitLabel={category ? 'Save changes' : 'Add category'}
    >
      <Field
        label="Course"
        required
        error={errors.courseKey}
        hint={locked ? 'Taken from the course selected at the top. Change it there to add under another course.' : undefined}
      >
        <select
          value={form.courseKey}
          disabled={locked}
          autoFocus={!locked}
          onChange={(e) => { setForm({ ...form, courseKey: e.target.value }); setErrors((p) => ({ ...p, courseKey: undefined })); }}
          className={`${inputClass(errors.courseKey)} disabled:bg-slate-100 disabled:text-slate-600`}
        >
          <option value="">Select a course…</option>
          {COURSE_PROGRAMMES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
      </Field>
      <Field label="Category name" required error={errors.name}>
        <input
          autoFocus={locked}
          type="text"
          value={form.name}
          maxLength={150}
          placeholder="e.g. HND Software"
          onChange={(e) => { setForm({ ...form, name: e.target.value }); setErrors((p) => ({ ...p, name: undefined })); }}
          className={inputClass(errors.name)}
        />
      </Field>
      {!category ? (
        <div className="-mt-2 flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" onClick={() => { setForm({ ...form, name: s }); setErrors((p) => ({ ...p, name: undefined })); }} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-600 hover:border-sky-300 hover:bg-sky-50 hover:text-sky-700">
              {s}
            </button>
          ))}
        </div>
      ) : null}
      <Field label="Description" error={errors.description} hint="Optional — a short note about this category.">
        <textarea rows={3} maxLength={500} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputClass(errors.description)} resize-none`} />
      </Field>
      <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
        <input type="checkbox" checked={form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} className="h-4 w-4 accent-sky-600" />
        <span className="text-sm text-slate-700">
          <span className="font-medium">Active</span>
          <span className="block text-xs text-slate-500">Turn off to keep the history but stop using this category.</span>
        </span>
      </label>
    </Modal>
  );
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 44,
    borderRadius: 8,
    borderColor: state.isFocused ? '#0284c7' : '#cbd5e1',
    boxShadow: state.isFocused ? '0 0 0 2px #e0f2fe' : 'none',
    '&:hover': { borderColor: state.isFocused ? '#0284c7' : '#94a3b8' },
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#e0f2fe', borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: '#075985', fontWeight: 600 }),
  menuPortal: (base) => ({ ...base, zIndex: 200 }),
  groupHeading: (base) => ({ ...base, fontWeight: 700, color: '#475569' }),
};

// Pick one or more batches that already exist in the system.
function BatchModal({ category, onClose, onSaved }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get(`${API}/available-batches`)
      .then((res) => { if (!cancelled) setOptions(res?.data?.data ?? []); })
      .catch(() => { if (!cancelled) setLoadError('Failed to load the batches.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Batches already in this category are left out; ones in another category are shown but locked.
  const groups = useMemo(() => {
    const byCourse = new Map();
    options
      .filter((b) => b.categoryId !== category.id)
      .forEach((b) => {
        const course = b.courseShortName || 'No course';
        if (!byCourse.has(course)) byCourse.set(course, []);
        byCourse.get(course).push({
          value: b.batchId,
          label: b.batchName,
          course,
          status: b.status,
          takenBy: b.categoryId ? b.categoryName : null,
          isDisabled: Boolean(b.categoryId),
        });
      });
    return [...byCourse.entries()].map(([label, items]) => ({ label, options: items }));
  }, [options, category.id]);

  const submit = async (e) => {
    e.preventDefault();
    if (saving) return;
    if (!picked.length) { Swal.fire({ icon: 'info', title: 'Select a batch', text: 'Choose at least one batch to add.' }); return; }
    setSaving(true);
    try {
      const res = await axiosInstance.post(`${API}/categories/${category.id}/batches`, { batchIds: picked.map((p) => p.value) });
      onSaved(res.data);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not add', text: err?.response?.data?.message || 'Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Add batches"
      subtitle={`Choose existing batches to place under ${category.name}.`}
      onClose={onClose}
      onSubmit={submit}
      saving={saving}
      submitLabel={picked.length ? `Add ${picked.length} ${picked.length === 1 ? 'batch' : 'batches'}` : 'Add batches'}
    >
      <Field label="Batches" required hint="Type to search. You can select several; they are grouped by course.">
        <Select
          isMulti
          autoFocus
          isLoading={loading}
          options={groups}
          value={picked}
          onChange={(v) => setPicked(v || [])}
          closeMenuOnSelect={false}
          hideSelectedOptions
          placeholder={loading ? 'Loading batches…' : 'Select batches…'}
          noOptionsMessage={() => loadError || 'No more batches available'}
          isOptionDisabled={(o) => o.isDisabled}
          filterOption={(o, input) => `${o.data.label} ${o.data.course}`.toLowerCase().includes(input.toLowerCase())}
          formatOptionLabel={(o) => (
            <div className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate">{o.label}</span>
              <span className="shrink-0 text-xs text-slate-400">
                {o.takenBy ? `In ${o.takenBy}` : STATUS_LABEL[o.status] || o.status}
              </span>
            </div>
          )}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={selectStyles}
        />
      </Field>
      {loadError ? <p className="text-xs text-rose-600">{loadError}</p> : null}
      <p className="text-xs text-slate-500">A batch can sit in one category only, so batches already in another category are greyed out.</p>
    </Modal>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
    </div>
  );
}

function BatchCard({ batch, onRemove }) {
  const start = formatDate(batch.startDate);
  const end = formatDate(batch.endDate);
  return (
    <li className="flex flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm transition hover:shadow-md">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h4 className="break-words text-sm font-semibold text-slate-900">{batch.batchName}</h4>
          {batch.courseShortName ? <p className="text-xs text-slate-500">{batch.courseShortName}</p> : null}
        </div>
        <span className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${STATUS_STYLE[batch.status] || STATUS_STYLE['not started']}`}>
          {STATUS_LABEL[batch.status] || batch.status}
        </span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-3 text-xs">
        <div><dt className="text-slate-400">Starts</dt><dd className="font-medium text-slate-700">{start || '—'}</dd></div>
        <div><dt className="text-slate-400">Ends</dt><dd className="font-medium text-slate-700">{end || '—'}</dd></div>
      </dl>
      <div className="mt-4 flex justify-end border-t border-slate-100 pt-3">
        <button type="button" onClick={() => onRemove(batch)} className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50">Remove</button>
      </div>
    </li>
  );
}

export default function TvecBatches() {
  const { courseId, course } = useTvecCourse();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [selectedId, setSelectedId] = useState(readSaved);
  const [search, setSearch] = useState('');
  const [batches, setBatches] = useState([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [filter, setFilter] = useState('All');
  const [categoryModal, setCategoryModal] = useState(null); // null | { category? }
  const [batchModal, setBatchModal] = useState(null); // null | { batch? }

  useEffect(() => {
    let cancelled = false;
    axiosInstance
      .get(`${API}/categories`)
      .then((res) => { if (!cancelled) setCategories(res?.data?.data ?? []); })
      .catch(() => { if (!cancelled) setLoadError('Failed to load categories.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Only the categories of the course chosen in the top bar ("All courses" shows everything).
  const scoped = useMemo(
    () => (courseId === ALL_COURSES_ID ? categories : categories.filter((c) => c.courseKey === courseId)),
    [categories, courseId],
  );

  // Keep a valid category selected whenever the course or the list changes.
  useEffect(() => {
    if (!scoped.length) { if (selectedId !== null) setSelectedId(null); return; }
    if (!scoped.some((c) => c.id === selectedId)) setSelectedId(scoped[0].id);
  }, [scoped, selectedId]);

  const selected = useMemo(() => scoped.find((c) => c.id === selectedId) || null, [scoped, selectedId]);

  useEffect(() => {
    if (!selectedId) { setBatches([]); return undefined; }
    writeSaved(selectedId);
    let cancelled = false;
    setBatchesLoading(true);
    setFilter('All');
    axiosInstance
      .get(`${API}/categories/${selectedId}/batches`)
      .then((res) => { if (!cancelled) setBatches(res?.data?.data ?? []); })
      .catch(() => { if (!cancelled) setBatches([]); })
      .finally(() => { if (!cancelled) setBatchesLoading(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  const upsertCategory = useCallback((category) => {
    setCategories((prev) => {
      const exists = prev.some((c) => c.id === category.id);
      const next = exists ? prev.map((c) => (c.id === category.id ? category : c)) : [...prev, category];
      return next.sort((a, b) => (a.courseKey || '').localeCompare(b.courseKey || '') || Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name));
    });
  }, []);

  const visibleCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? scoped.filter((c) => c.name.toLowerCase().includes(q)) : scoped;
  }, [scoped, search]);

  const counts = useMemo(() => {
    const c = { All: batches.length };
    STATUSES.forEach((s) => { c[s] = batches.filter((b) => b.status === s).length; });
    return c;
  }, [batches]);
  const visibleBatches = filter === 'All' ? batches : batches.filter((b) => b.status === filter);

  const saveCategory = (category) => {
    const isNew = !categories.some((c) => c.id === category.id);
    upsertCategory(category);
    if (isNew) setSelectedId(category.id);
    setCategoryModal(null);
  };

  const saveBatches = ({ added, skipped, category }) => {
    if (category) upsertCategory(category);
    setBatchModal(null);
    // Reload the list so the new batches appear with their details.
    axiosInstance.get(`${API}/categories/${selectedId}/batches`).then((res) => setBatches(res?.data?.data ?? [])).catch(() => {});
    if (skipped) Swal.fire({ icon: 'info', title: `${added} added`, text: `${skipped} skipped because they already belong to another category.` });
  };

  const removeBatch = async (batch) => {
    const result = await Swal.fire({ icon: 'warning', title: 'Remove this batch?', text: `${batch.batchName} will be taken out of ${selected.name}. The batch itself is not deleted.`, showCancelButton: true, confirmButtonText: 'Remove', confirmButtonColor: '#dc2626' });
    if (!result.isConfirmed) return;
    try {
      const res = await axiosInstance.delete(`${API}/categories/${selected.id}/batches/${batch.batchId}`);
      setBatches((prev) => prev.filter((b) => b.batchId !== batch.batchId));
      if (res.data.category) upsertCategory(res.data.category);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Could not remove', text: err?.response?.data?.message || 'Please try again.' });
    }
  };

  const removeCategory = async () => {
    if (!selected) return;
    const result = await Swal.fire({ icon: 'warning', title: 'Delete this category?', text: selected.name, showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#dc2626' });
    if (!result.isConfirmed) return;
    try {
      await axiosInstance.delete(`${API}/categories/${selected.id}`);
      setCategories((prev) => prev.filter((c) => c.id !== selected.id));
      setSelectedId(null);
    } catch (err) {
      Swal.fire({ icon: 'info', title: 'Cannot delete yet', text: err?.response?.data?.message || 'Please try again.' });
    }
  };

  return (
    <TvecLayout title="Batches">
      <div className="w-full space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Categories &amp; batches</h2>
            <p className="text-sm text-slate-500">
              Showing <span className="font-semibold text-sky-700">{course.label}</span>. Add a category such as HND Software, then pick the existing batches that belong under it.
            </p>
          </div>
          <button type="button" onClick={() => setCategoryModal({})} className="rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sky-700">
            + Add category
          </button>
        </div>

        {loadError ? <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{loadError}</p> : null}

        {loading ? (
          <div className="grid gap-5 lg:grid-cols-[18rem_minmax(0,1fr)]">
            <div className="h-64 animate-pulse rounded-2xl border border-slate-200 bg-white" />
            <div className="h-64 animate-pulse rounded-2xl border border-slate-200 bg-white" />
          </div>
        ) : scoped.length === 0 && !loadError ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
            <p className="text-base font-semibold text-slate-900">
              {courseId === ALL_COURSES_ID ? 'No categories yet' : `No categories under ${course.label} yet`}
            </p>
            <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
              {courseId === ALL_COURSES_ID
                ? 'Start by adding a category and choosing its course. You can pick its batches right after.'
                : 'Add a category, for example HND Software or HND Networking. You can pick its batches right after.'}
            </p>
            <button type="button" onClick={() => setCategoryModal({})} className="mt-5 rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700">+ Add your first category</button>
          </div>
        ) : (
          <div className="grid items-start gap-5 lg:grid-cols-[18rem_minmax(0,1fr)]">
            {/* Categories */}
            <aside className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm lg:sticky lg:top-0">
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search categories…"
                className="mb-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-100"
              />
              <ul className="flex gap-2 overflow-x-auto pb-1 lg:max-h-[60vh] lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden lg:pb-0">
                {visibleCategories.map((c) => {
                  const active = c.id === selectedId;
                  return (
                    <li key={c.id} className="shrink-0 lg:shrink">
                      <button
                        type="button"
                        onClick={() => setSelectedId(c.id)}
                        aria-current={active ? 'true' : undefined}
                        className={`w-full min-w-[10rem] rounded-xl border px-3 py-2.5 text-left transition ${active ? 'border-sky-500 bg-sky-50 ring-1 ring-sky-500' : 'border-slate-200 hover:border-sky-300 hover:bg-slate-50'}`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          <span className={`truncate text-sm font-semibold ${active ? 'text-sky-800' : 'text-slate-800'}`}>{c.name}</span>
                          {!c.isActive ? <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-slate-600">Inactive</span> : null}
                        </span>
                        {courseId === ALL_COURSES_ID ? <span className="mt-1 block"><CourseChip courseKey={c.courseKey} /></span> : null}
                        <span className="mt-0.5 block text-xs text-slate-500">
                          {c.batchCount} {c.batchCount === 1 ? 'batch' : 'batches'}{c.ongoingCount ? ` · ${c.ongoingCount} ongoing` : ''}
                        </span>
                      </button>
                    </li>
                  );
                })}
                {visibleCategories.length === 0 ? <li className="px-2 py-4 text-center text-xs text-slate-500">No category matches.</li> : null}
              </ul>
            </aside>

            {/* Selected category */}
            {selected ? (
              <section className="min-w-0 space-y-4">
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="break-words text-xl font-bold text-slate-900">{selected.name}</h3>
                      <div className="mt-1"><CourseChip courseKey={selected.courseKey} /></div>
                      {selected.description ? <p className="mt-1 text-sm text-slate-500">{selected.description}</p> : null}
                    </div>
                    <div className="flex gap-1.5">
                      <button type="button" onClick={() => setCategoryModal({ category: selected })} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100">Edit</button>
                      <button type="button" onClick={removeCategory} className="rounded-lg border border-rose-200 px-3 py-1.5 text-xs font-semibold text-rose-600 hover:bg-rose-50">Delete</button>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3 sm:max-w-sm">
                    <Stat label="Batches" value={selected.batchCount} />
                    <Stat label="Ongoing" value={selected.ongoingCount} />
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div role="tablist" aria-label="Filter batches" className="flex gap-1 overflow-x-auto rounded-lg bg-slate-100 p-1">
                    {['All', ...STATUSES].map((s) => (
                      <button key={s} type="button" role="tab" aria-selected={filter === s} onClick={() => setFilter(s)} className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold transition ${filter === s ? 'bg-white text-sky-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                        {STATUS_LABEL[s] || s} <span className="ml-0.5 text-slate-400">{counts[s]}</span>
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={() => setBatchModal({})} className="rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-sky-700">+ Add batch</button>
                </div>

                {batchesLoading ? (
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {[0, 1, 2].map((i) => <div key={i} className="h-36 animate-pulse rounded-2xl border border-slate-200 bg-white" />)}
                  </div>
                ) : batches.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center">
                    <p className="text-sm font-semibold text-slate-900">No batches under {selected.name} yet</p>
                    <p className="mt-1 text-sm text-slate-500">Pick from the batches that already exist in the system.</p>
                    <button type="button" onClick={() => setBatchModal({})} className="mt-4 rounded-lg bg-sky-600 px-4 py-2 text-xs font-semibold text-white hover:bg-sky-700">+ Add batch</button>
                  </div>
                ) : visibleBatches.length === 0 ? (
                  <p className="rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500">No batches with this status.</p>
                ) : (
                  <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                    {visibleBatches.map((b) => (
                      <BatchCard key={b.batchId} batch={b} onRemove={removeBatch} />
                    ))}
                  </ul>
                )}
              </section>
            ) : null}
          </div>
        )}
      </div>

      {categoryModal ? <CategoryModal category={categoryModal.category} defaultCourse={courseId} onClose={() => setCategoryModal(null)} onSaved={saveCategory} /> : null}
      {batchModal && selected ? <BatchModal category={selected} onClose={() => setBatchModal(null)} onSaved={saveBatches} /> : null}
    </TvecLayout>
  );
}
